import { useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Slider } from "../ui/slider";

const OUTPUT_SIZE = 1200;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load the photo for cropping"));
    img.src = src;
  });
}

async function cropToBlob(src, area) {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not create the cropped photo"))), "image/jpeg", 0.9);
  });
}

export default function CropDialog({ src, open, onClose, onCropped, aspect = 1 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setArea(null); };

  const apply = async () => {
    if (!area) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(src, area);
      const fd = new FormData();
      fd.append("file", new File([blob], "cropped.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/admin/upload", fd);
      onCropped(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`);
      toast.success("Photo cropped");
      reset();
      onClose();
    } catch (err) {
      toast.error(err.response ? formatApiErrorDetail(err.response.data?.detail) : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Crop photo</DialogTitle>
          <DialogDescription>Drag to position and use the slider to zoom. The square is exactly what visitors will see on the homepage.</DialogDescription>
        </DialogHeader>
        <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-stone-900" data-testid="crop-area">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, px) => setArea(px)}
              showGrid={false}
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-stone-500">Zoom</span>
          <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={([v]) => setZoom(v)} data-testid="crop-zoom-slider" className="flex-1" />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => { reset(); onClose(); }} disabled={busy} data-testid="crop-cancel-button" className="rounded-lg px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-lavender-100">
            Cancel
          </button>
          <button type="button" onClick={apply} disabled={busy || !area} data-testid="crop-apply-button" className="inline-flex items-center gap-2 rounded-lg bg-lavender-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-lavender-800 disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Saving…" : "Save crop"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
